import {
  ActionListItem,
  FieldErrors,
  Link,
  Stack,
  Text,
} from "@certn/cascadia";
import {
  convertDataToDisplayValues,
  getItemList,
} from "@certn/forms/src/controls/utils";
import { useContext } from "react";
import { useTranslation } from "react-i18next";

import { ArrayModalControlContext } from "./ArrayModalControlContext";
import { ArrayModalControlDialog } from "./ArrayModalControlDialog";

/**
 * Props for the ArrayModalControlListItem component.
 */
interface Props {
  /** The item data to be displayed and managed. */
  item: Record<string, unknown>;

  /** The path to the item being managed. */
  itemPath: string;

  /** Validation errors associated with the item. */
  errors: Record<string, string[]>;

  /** A callback function invoked when the form is submitted. */
  onSubmit: (itemPath: string, values: unknown) => Promise<void>;

  /** An optional callback function invoked when the delete action is triggered. */
  onDelete?: (itemPath: string) => Promise<void>;
}

/**
 * A list item component for managing array items within a modal.
 * This component is intended to be used with {@link ArrayModalControl}.
 */
export function ArrayModalControlListItem(props: Props) {
  const { item, itemPath, errors } = props;
  const { t } = useTranslation();
  const context = useContext(ArrayModalControlContext);

  const { label, childSchema, options, renderTag, renderSubtitle } = context;

  const itemDisplayValues = convertDataToDisplayValues(item, childSchema);

  const title = getItemList(
    [
      typeof options?.elementLabel === "string"
        ? options?.elementLabel
        : Object.keys(item)[0],
    ],
    itemDisplayValues
  ).filter(Boolean)[0];

  const subtitles = getItemList(
    Array.isArray(options?.elementSubLabels) &&
      options.elementSubLabels.every((label) => typeof label === "string")
      ? options.elementSubLabels
      : Object.keys(item),
    itemDisplayValues
  ).filter(Boolean);

  return (
    <ActionListItem
      tag={renderTag && renderTag(item)}
      title={title}
      inset
      trailing={
        <ArrayModalControlDialog
          itemPath={itemPath}
          title={t("Edit {{label}}", { label })}
          initialValues={item}
          trigger={({ toggle }) => (
            <Link
              onPress={toggle}
              linkStyle="monochrome"
              textColor="text-weak"
              testID="edit-item-link"
            >
              {t("Edit")}
            </Link>
          )}
          onSubmit={props.onSubmit}
          onDelete={props.onDelete}
        />
      }
    >
      <Stack gap="space-1">
        {!options?.hideSubLabel && renderSubtitle && renderSubtitle(item)}
        {!options?.hideSubLabel &&
          !renderSubtitle &&
          subtitles.map((label: string) => (
            <Text key={label} style="global/body-secondary" color="text-weak">
              {label}
            </Text>
          ))}
        <FieldErrors>{Object.values(errors)}</FieldErrors>
      </Stack>
    </ActionListItem>
  );
}
